import { useEffect, useRef, useState } from "react";

import type { Shape, Slide } from "@/lib/types";
import type { MediaAsset } from "@/lib/types";

type Props = {
  slide: Slide | null;
  media?: MediaAsset[];
  widthPx?: number;
};

const SLIDE_W_EMU = 12192000;
const SLIDE_H_EMU = 6858000;
const ASPECT_RATIO = SLIDE_H_EMU / SLIDE_W_EMU;

function base64ToDataUrl(data: string, mime: string): string {
  return `data:${mime};base64,${data}`;
}

function shapeText(shape: Shape): string {
  const tf = shape.text_frame as
    | { paragraphs?: Array<{ runs?: Array<{ text?: string }> }> }
    | null
    | undefined;
  if (!tf?.paragraphs) return "";
  return tf.paragraphs
    .map((p) => (p.runs ?? []).map((r) => r.text ?? "").join(""))
    .filter((s) => s.length > 0)
    .join("\n");
}

function fillColorCss(shape: Shape): string | undefined {
  const fill = shape.fill as
    | { type?: string; color?: { kind?: string; value?: string } }
    | null
    | undefined;
  if (!fill || fill.type !== "solid") return undefined;
  const color = fill.color;
  if (color?.kind === "rgb" && typeof color.value === "string") {
    return `#${color.value}`;
  }
  return undefined;
}

export function SlidePreview({ slide, media, widthPx }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);

  useEffect(() => {
    if (widthPx) return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = e.contentRect.width;
        if (w > 0) setMeasuredWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [widthPx]);

  const effectiveWidth = widthPx ?? measuredWidth ?? 0;
  const scale = effectiveWidth > 0 ? effectiveWidth / SLIDE_W_EMU : 0;
  const heightPx = effectiveWidth > 0 ? effectiveWidth * ASPECT_RATIO : 0;

  const mediaByRef: Record<string, MediaAsset> = {};
  for (const m of media ?? []) mediaByRef[m.ref] = m;

  const outerStyle: React.CSSProperties = widthPx
    ? { width: widthPx, maxWidth: "100%" }
    : { width: "100%" };

  if (!slide) {
    return (
      <div
        ref={containerRef}
        className="flex items-center justify-center rounded-[10px] border border-dashed border-[var(--color-border)] bg-[var(--color-surface-muted)] text-sm text-[var(--color-ink-soft)]"
        style={{ ...outerStyle, aspectRatio: "16 / 9" }}
      >
        スライド未選択
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden rounded-[10px] border border-[var(--color-border)] bg-white shadow-[var(--shadow-sm)]"
      style={{
        ...outerStyle,
        aspectRatio: "16 / 9",
        height: heightPx || undefined,
      }}
      aria-label={`slide-${slide.slide_index} preview`}
    >
      {scale > 0 &&
        slide.shapes.map((shape, idx) => {
          const t = shape.transform;
          const left = t.left * scale;
          const top = t.top * scale;
          const width = t.width * scale;
          const height = t.height * scale;
          const bg = fillColorCss(shape);
          const text = shapeText(shape);
          const pictureRef = typeof shape.picture_ref === "string" ? shape.picture_ref : null;
          const mediaAsset = pictureRef ? mediaByRef[pictureRef] : undefined;

          return (
            <div
              key={`${slide.slide_index}-${shape.id}-${idx}`}
              className="absolute overflow-hidden"
              style={{
                left,
                top,
                width,
                height,
                transform: t.rotation ? `rotate(${t.rotation}deg)` : undefined,
                background: bg,
                border: shape.type === "picture" ? undefined : "1px dashed rgba(0,0,0,0.08)",
              }}
            >
              {shape.type === "picture" && mediaAsset && mediaAsset.data_base64 ? (
                <img
                  src={base64ToDataUrl(mediaAsset.data_base64, mediaAsset.mime)}
                  alt={mediaAsset.ref}
                  className="h-full w-full object-contain"
                />
              ) : null}
              {text ? (
                <div
                  className="h-full w-full overflow-hidden break-words px-1 leading-tight"
                  style={{ color: "#1F1D1A", fontSize: Math.max(7, scale * 140000) }}
                >
                  {text}
                </div>
              ) : null}
            </div>
          );
        })}
      <div className="absolute bottom-0 right-0 rounded-tl-[6px] bg-[var(--color-ink)]/50 px-1.5 py-0.5 text-[9px] text-white">
        #{slide.slide_index}
      </div>
    </div>
  );
}
